import { savePosition } from "../../ReusableFunctions/playbackPosition";
import { getPositionSeconds } from "./positionSeconds";

/*
 * Cross-device position sync (Phase 6). localStorage remains the fast path;
 * when signed in, positions also flow through the Laravel progress service:
 *   GET  /user/positions        — read on load, furthest-wins merge
 *   POST /user/positions        — debounced writes (pause / 15s / hide)
 *   GET  /user/series-progress  — per-series completed/current rollup
 * Every call degrades gracefully (null) until the backend deploys — callers
 * fall back to the device-local behavior that shipped in earlier phases.
 */

const BASE = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";

function authHeaders() {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export function isSignedIn() {
  return Boolean(localStorage.getItem("accessToken"));
}

let positionsCache = null; // { at: epoch-ms, rows: [...] }
const CACHE_MS = 60_000;

/** @returns {Promise<Array|null>} server position rows, or null when unavailable */
export async function fetchPositions({ fresh = false } = {}) {
  const headers = authHeaders();
  if (!headers) return null;
  if (!fresh && positionsCache && Date.now() - positionsCache.at < CACHE_MS) {
    return positionsCache.rows;
  }
  try {
    const res = await fetch(`${BASE}/user/positions`, { headers });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json?.status || !Array.isArray(json.data)) return null;
    positionsCache = { at: Date.now(), rows: json.data };
    // Furthest-wins merge into device storage so every player surface
    // (which reads localStorage by media URL) sees the synced position.
    for (const row of json.data) {
      const url = row.audio_stream_url || row.audio_src;
      if (url && row.position_seconds > 0) {
        const local = getPositionSeconds(url);
        if (row.position_seconds > local) savePosition(url, row.position_seconds);
      }
    }
    return json.data;
  } catch (_) {
    return null;
  }
}

let pushTimer = null;
let pendingByClass = new Map();

/** Debounced batch write; safe to call on every pause/heartbeat. */
export function pushPosition({ classId, positionSeconds, mediaKind }) {
  if (!classId || !(positionSeconds > 0) || !isSignedIn()) return;
  pendingByClass.set(classId, {
    class_id: classId,
    position_seconds: Math.floor(positionSeconds),
    media_kind: mediaKind || "audio",
  });
  clearTimeout(pushTimer);
  pushTimer = setTimeout(flushPositions, 3000);
}

export async function flushPositions() {
  clearTimeout(pushTimer);
  const headers = authHeaders();
  if (!headers || pendingByClass.size === 0) return;
  const positions = [...pendingByClass.values()];
  pendingByClass = new Map();
  try {
    await fetch(`${BASE}/user/positions`, {
      method: "POST",
      headers,
      body: JSON.stringify({ positions }),
      keepalive: true,
    });
    positionsCache = null; // next read sees fresh data
  } catch (_) {
    /* device-local storage still has it; retried on the next pause */
  }
}

/** @returns {Promise<object|null>} series rollup {total, completed_count, current_class_id, classes[]} */
export async function fetchSeriesProgress(node) {
  const headers = authHeaders();
  if (!headers || !node) return null;
  try {
    const res = await fetch(`${BASE}/user/series-progress?node=${encodeURIComponent(node)}`, { headers });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.status ? json.data : null;
  } catch (_) {
    return null;
  }
}
